/**
 * @license
 * Copyright Piotr Stępniewski <k3nsei.pl@gmail.com> All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://raw.githubusercontent.com/k3nsei/ng-in-viewport/stable/LICENSE
 */

export enum InViewportConfigDirection {
  BOTH = 'both',
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export interface InViewportConfigCheckFnOptions {
  force: boolean;
  config: InViewportConfig;
}

export type InViewportConfigCheckFn = (
  entry: IntersectionObserverEntry | undefined,
  options: InViewportConfigCheckFnOptions,
) => any;

export interface InViewportConfigOptions {
  root?: Element;
  rootMargin?: string;
  threshold?: number | number[];
  partial?: boolean;
  direction?: InViewportConfigDirection;
  checkFn?: InViewportConfigCheckFn;
}

const isPlatformBrowser = new Function(`
  try {
    return typeof window !== 'undefined' && this === window;
  } catch(e) {
    return false;
  }
`);

const toBase64 = !isPlatformBrowser()
  ? new Function('input', `return Buffer.from(input).toString('base64')`)
  : new Function('input', 'return btoa(input)');

export class InViewportConfig {
  private static get DEFAULT_THRESHOLD() {
    return [0, 1] as const;
  }

  private static get STRINGIFY_DELIMITER() {
    return '|' as const;
  }

  private _root: Element | undefined;

  private _rootMargin: string = '0px 0px 0px 0px';

  private _threshold: number | number[] = [...InViewportConfig.DEFAULT_THRESHOLD];

  private _partial: boolean = true;

  private _direction: InViewportConfigDirection = InViewportConfigDirection.BOTH;

  private _checkFn: InViewportConfigCheckFn | undefined;

  private readonly _hash: string;

  private static stringify(input: Record<string, any>): string {
    if (Array.isArray(input)) {
      const stringifiedArr = [];

      for (let i = 0; i < input.length; i++) {
        stringifiedArr.push(InViewportConfig.stringify(input[i]));
      }

      return `[${stringifiedArr.join(',')}]`;
    } else if (typeof input === 'object' && input !== null) {
      const acc = [];
      const sortedKeys = Object.keys(input).sort();

      for (const k of sortedKeys) {
        const v = InViewportConfig.stringify(input[k]);

        acc.push(`${k}:${v}`);
      }

      return acc.join(InViewportConfig.STRINGIFY_DELIMITER);
    }

    return String(input);
  }

  private static hash(input: object): string {
    return toBase64(InViewportConfig.stringify(input));
  }

  constructor(options?: InViewportConfigOptions) {
    if (Object.prototype.toString.call(options) === '[object Object]') {
      (['root', 'rootMargin', 'threshold', 'partial', 'direction', 'checkFn'] as const).forEach((prop) => {
        if (options?.hasOwnProperty(prop)) {
          // @ts-ignore setters do value validation
          this[prop] = options[prop];
        }
      });
    }

    this._hash = InViewportConfig.hash({
      rootMargin: this.rootMargin,
      threshold: this.threshold,
      partial: this.partial,
      direction: this.direction,
      checkFn: String(this.checkFn),
    });
  }

  public get root(): Element | undefined {
    return this._root;
  }

  public set root(value: Element | undefined) {
    this._root = value && value.nodeType === 1 ? value : undefined;
  }

  public get rootMargin(): string {
    return this._rootMargin;
  }

  public set rootMargin(value: string) {
    if (!value || typeof value !== 'string') {
      this._rootMargin = '0px 0px 0px 0px';
    } else {
      const marginString: string = value || '0px';
      const margins: string[] = marginString.split(new RegExp('\\s+')).map((margin) => {
        const parts = /^(-?\d*\.?\d+)(px|%)$/.exec(margin);
        if (!parts) {
          throw new TypeError('rootMargin must be specified in pixels or percent');
        }
        return `${parts[1]}${parts[2]}`;
      });

      margins[1] = margins[1] || margins[0];
      margins[2] = margins[2] || margins[0];
      margins[3] = margins[3] || margins[1];

      this._rootMargin = margins.join(' ');
    }
  }

  public get threshold(): number | number[] {
    return this._threshold;
  }

  public set threshold(value: number | number[]) {
    let threshold: number[] = [];
    const isValidThreshold = (val: any): val is number => typeof val === 'number' && val >= 0 && val <= 1;
    if (isValidThreshold(value)) {
      threshold = [value];
    } else if (Array.isArray(value) && value.length) {
      threshold = value.filter((val) => isValidThreshold(val));
    }
    if (threshold.length === 0) {
      threshold = [...InViewportConfig.DEFAULT_THRESHOLD];
    }
    this._threshold = threshold;
  }

  public get partial(): boolean {
    return this._partial;
  }

  public set partial(value: boolean) {
    this._partial = !!value;
  }

  public get direction(): InViewportConfigDirection {
    return this._direction;
  }

  public set direction(value: InViewportConfigDirection) {
    const isValidValue = (val: any) => {
      return (
        [
          InViewportConfigDirection.BOTH,
          InViewportConfigDirection.HORIZONTAL,
          InViewportConfigDirection.VERTICAL,
        ].indexOf(val) >= 0
      );
    };
    this._direction = isValidValue(value) ? value : InViewportConfigDirection.BOTH;
  }

  public get hash(): string {
    return this._hash;
  }

  public get checkFn(): InViewportConfigCheckFn | undefined {
    return this._checkFn;
  }

  public set checkFn(value: InViewportConfigCheckFn | undefined) {
    this._checkFn = value;
  }
}
